import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

/*
  `secondary` je nosio `border-line-strong` — providan rub koji na bijeloj plohi
  daje 1,48:1. Natpis je bio citljiv, ali granica gumba nije se vidjela, pa se
  gumb morao traziti (najgore kod „Slikaj" u obrascu obroka). WCAG 1.4.11 trazi
  3:1 za granicu kontrole; `line-control` daje 4,09:1.

  Ispuna ostaje prozirna i natpis ostaje `ink` — hijerarhija se ne mijenja,
  glavni gumb je i dalje jedini ispunjen.
*/
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-accent-dark text-surface hover:bg-accent-deep',
  secondary: 'bg-transparent text-ink border border-line-control hover:bg-bg-alt',
  ghost: 'bg-transparent text-ink hover:bg-bg-alt',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-[13px]',
  md: 'px-6 py-3 text-[14.5px]',
  lg: 'px-10 py-4 text-[15px]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  block?: boolean;
  leading?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  leading,
  className,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-pill font-body tracking-[0.3px]',
        'transition-colors duration-200',
        VARIANT[variant],
        SIZE[size],
        block && 'w-full',
        isDisabled && 'opacity-40 pointer-events-none',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : leading}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
